import { STATE_ABBREVIATIONS } from 'shared/constants/property';
import { RentTypesUnitsOfMeasure } from '../constants';
import { CompplexCommercialLeaseOutput } from './types';
import { isEmpty, isNil, lowerCase } from 'lodash';
import { CommercialRentComp, CommercialUses } from 'types/underwriting/income-approach';

const transformBaseRent = (baseRent: number, unitOfMeasure: string, unitSquareFootage: number) => {
  let annualRent = baseRent;
  let monthlyRent = baseRent;
  let annualRentPerSF = baseRent;
  let mappedUnitOfMeasure = lowerCase(unitOfMeasure);
  if (mappedUnitOfMeasure === 'per square foot') {
    mappedUnitOfMeasure = RentTypesUnitsOfMeasure.PER_SF_PER_YEAR;
  }
  switch (mappedUnitOfMeasure) {
    case RentTypesUnitsOfMeasure.ANNUAL:
      monthlyRent = baseRent / 12;
      annualRentPerSF = baseRent / unitSquareFootage;
      break;
    case RentTypesUnitsOfMeasure.MONTHLY:
      annualRent = baseRent * 12;
      annualRentPerSF = annualRent / unitSquareFootage;
      break;
    case RentTypesUnitsOfMeasure.PER_SF_PER_MONTH:
      annualRent = baseRent * 12 * unitSquareFootage;
      annualRentPerSF = baseRent * 12;
      break;
    case RentTypesUnitsOfMeasure.PER_SF_PER_YEAR:
      annualRent = baseRent * unitSquareFootage * 12;
      monthlyRent = annualRent / 12;
      annualRentPerSF = annualRent / unitSquareFootage;
      break;
    default:
      break;
  }
  return { monthlyRent, annualRent, annualRentPerSF, mappedUnitOfMeasure };
};

export const mapCompPlexCommercialLeaseToRentComp = (
  commercialRentComp: CompplexCommercialLeaseOutput
): CommercialRentComp => {
  const {
    id,
    version,
    isLatestVersion,
    address,
    tenantName,
    unitType,
    unitSquareFootage,
    leaseInformation,
    leaseDate,
    status,
    propertyInformation,
  } = commercialRentComp;
  const { postalCode, state } = address;
  const { displayStreetAddress: streetAddress, displayCity: city } = propertyInformation;

  const { unitOfMeasure, leaseTerms, leaseStatus, leaseExpirationDate, baseRent } = leaseInformation;

  const { monthlyRent, annualRent, annualRentPerSF, mappedUnitOfMeasure } = transformBaseRent(
    baseRent,
    unitOfMeasure,
    unitSquareFootage
  );

  const UNIT_TYPE_MAP: { [key in string]: CommercialUses } = {
    medicalOffice: CommercialUses.MEDICAL,
    communityFacility: CommercialUses.COMMUNITY,
    retail: CommercialUses.RETAIL,
    office: CommercialUses.OFFICE,
    industrial: CommercialUses.INDUSTRIAL,
    undetermined: CommercialUses.UNDETERMINED,
  };
  const mappedUse: CommercialUses = UNIT_TYPE_MAP[unitType] || CommercialUses.UNDETERMINED;

  return {
    leaseId: id,
    leaseVersionNumber: version,
    status,
    isLatestVersion,
    address: streetAddress,
    city,
    state,
    zip: postalCode,
    fullAddressWithZip: `${streetAddress}, ${city}, ${STATE_ABBREVIATIONS[state]} ${postalCode}`,
    tenantName,
    rentType: mappedUnitOfMeasure,
    use: mappedUse,
    squareFeet: unitSquareFootage,
    monthlyRent,
    annualRent,
    annualRentPerSF,
    lease: {
      expiresAt: !isNil(leaseExpirationDate) && !isEmpty(leaseExpirationDate) ? leaseExpirationDate : undefined,
      signedAt: leaseDate,
      status: leaseStatus,
      terms: lowerCase(leaseTerms),
    },
  };
};
